import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, session, openLeagueSocket } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Zap, Shuffle, Clock, CheckCircle2, Crown } from "lucide-react";
import TeamsPerPlayerCard from "@/components/TeamsPerPlayerCard";
import { getStoredViewTournament } from "@/components/dashboard/TournamentSwitcher";

export default function DraftRoom() {
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [currentPickerId, setCurrentPickerId] = useState(null);
    const [me, setMe] = useState(null);
    // iter63z'' — Default to "auto" since Live Snake is hidden by default.
    // See /app/memory/LIVE_DRAFT_HIDDEN.md for restoration.
    const [mode, setMode] = useState("auto");

    const sessionData = session.load();

    const load = useCallback(async () => {
        // iter48 — Honor the dashboard's currently-viewed tournament when
        // loading the draft page. Without this, the page reads
        // league.tournament_id (legacy default) and shows the wrong
        // tournament's team count when the league has multiple tournaments.
        const viewedTid = getStoredViewTournament(leagueId);
        const d = await api.getLeague(leagueId, { tournamentId: viewedTid });
        setData(d);
        const myP = d.participants.find(p => p.id === sessionData?.participant_id);
        setMe(myP);
        if (d.draft) {
            const r = await api.getDraft(d.draft.id);
            setDraft(r.draft);
            setCurrentPickerId(r.current_picker_id);
        }
    }, [leagueId, sessionData?.participant_id]);

    useEffect(() => {
        if (!sessionData || sessionData.league_id !== leagueId) {
            navigate("/");
            return;
        }
        load();
        const sock = openLeagueSocket(leagueId, (msg) => {
            if (["draft_pick", "draft_started"].includes(msg.event)) load();
        });
        const interval = setInterval(load, 8000); // polling fallback
        return () => { sock.close(); clearInterval(interval); };
    }, [leagueId, load, navigate, sessionData]);

    if (!data) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Loading...</div>;
    if (!data.tournament) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3">
                <p className="text-zinc-400">Create a tournament first.</p>
                <Link to={`/league/${leagueId}/setup`} className="brutal-btn-primary">Set up</Link>
            </div>
        );
    }

    const teamsById = Object.fromEntries(data.tournament.teams.map(t => [t.id, t]));
    const participantsById = Object.fromEntries(data.participants.map(p => [p.id, p]));

    const handleStart = async () => {
        if (!me?.is_admin) return;
        try {
            // iter50b — Send the currently-viewed tournament_id so the draft
            // runs against the SAME tournament shown on the dashboard, not
            // whatever the league's legacy `tournament_id` pointer happens
            // to be. Critical for multi-tournament leagues where the viewed
            // tournament may differ from `league.tournament_id`.
            const d = await api.startDraft({
                league_id: leagueId, mode,
                // iter86.31.4 — Use the tournament ACTUALLY loaded on this
                // page, not the raw localStorage view pointer. After a Site
                // Admin deletes a tournament, the stored pointer can be
                // stale; getLeague already fell back to a valid tournament,
                // so trust what's rendered.
                ...(data?.tournament?.id ? { tournament_id: data.tournament.id } : {}),
            });
            toast.success(mode === "auto" ? "Teams auto-assigned!" : "Draft started");
            // Surface auto-clamping (e.g. requested 3 teams/player but only 6 teams + 7 players → portfolio mode at 2)
            if (d.clamp_info) {
                const ci = d.clamp_info;
                toast.warning(
                    `Heads up: teams-per-crewmate auto-adjusted from ${ci.requested_slots} to ${ci.effective_slots}.`,
                    {
                        description: `${ci.player_count} players · ${ci.team_count} teams — using ${ci.effective_slots} team${ci.effective_slots === 1 ? "" : "s"} per crewmate.`,
                        duration: 8000,
                    },
                );
            }
            setDraft(d);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const handlePick = async (teamId) => {
        if (!draft) return;
        try {
            const r = await api.makePick(draft.id, { participant_id: me.id, team_id: teamId });
            setDraft(r.draft);
            setCurrentPickerId(r.current_picker_id);
            toast.success("Pick locked");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    // PRE-DRAFT (admin chooses mode and starts)
    if (!draft) {
        return (
            <div className="min-h-screen px-4 lg:px-12 py-8 max-w-4xl mx-auto">
                <Link to={`/league/${leagueId}`} className="brutal-btn-ghost mb-6 inline-flex" data-testid="draft-back-btn">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
                <h1 className="font-display font-black text-4xl mb-6">Choose Draft Mode</h1>

                {!me?.is_admin && (
                    <div className="brutal-card p-6 text-center">
                        <Clock className="w-10 h-10 mx-auto text-cyber mb-2" />
                        <p>Waiting for the admin to start the draft.</p>
                    </div>
                )}

                {me?.is_admin && (
                    <>
                        <div className="grid sm:grid-cols-2 gap-3 mb-6">
                            {[
                                // iter63z'' — Live Snake mode hidden by default. Restoration:
                                // see /app/memory/LIVE_DRAFT_HIDDEN.md. The backend mode + WS
                                // events are intact; we just don't surface the picker option.
                                // Site Admin can re-expose via the `live_draft_enabled` flag
                                // (system_settings.feature_flags) once we're ready.
                                ...(data?.feature_flags?.live_draft_enabled ? [
                                    { k: "live", icon: Zap, label: "Live Snake", desc: "Turn-based, real-time picks", color: "text-cyber" },
                                ] : []),
                                { k: "auto", icon: Shuffle, label: "Auto Draft Now", desc: "Tier-balanced, one-tap", color: "text-neon" },
                            ].map((m) => (
                                <button key={m.k}
                                    data-testid={`mode-${m.k}-btn`}
                                    onClick={() => setMode(m.k)}
                                    className={`text-left p-5 border-2 rounded-md ${mode === m.k ? "border-cyber bg-cyber/10 shadow-brutalYellow" : "border-[#333] bg-ink"}`}>
                                    <m.icon className={`w-6 h-6 ${m.color} mb-2`} strokeWidth={2.5} />
                                    <p className="font-display font-extrabold text-lg">{m.label}</p>
                                    <p className="text-xs text-zinc-500 mt-1">{m.desc}</p>
                                </button>
                            ))}
                        </div>
                        <TeamsPerPlayerCard
                            league={data.league}
                            participants={data.participants}
                            tournament={data.tournament}
                            onChanged={load}
                        />
                        <button data-testid="start-draft-btn" onClick={handleStart} className="brutal-btn-primary text-lg mt-4">
                            <Zap className="w-5 h-5" strokeWidth={3} /> Start {mode} draft
                        </button>
                        <p className="text-xs text-zinc-500 mt-3" data-testid="draft-room-footer-summary">{data.participants.filter(p => p.is_player !== false).length} players · {data.tournament.teams.length} teams</p>
                    </>
                )}
            </div>
        );
    }

    // DRAFT COMPLETE
    if (draft.status === "complete") {
        const picksByParticipant = draft.picks.reduce((acc, pk) => {
            if (!acc[pk.participant_id]) acc[pk.participant_id] = [];
            acc[pk.participant_id].push(pk.team_id);
            return acc;
        }, {});
        return (
            <div className="min-h-screen px-4 lg:px-12 py-8 max-w-5xl mx-auto">
                <Link to={`/league/${leagueId}`} className="brutal-btn-ghost mb-6 inline-flex" data-testid="draft-back-btn-2">
                    <ArrowLeft className="w-4 h-4" /> Back
                </Link>
                <div className="brutal-card p-6 mb-6 bg-neon/10 border-neon">
                    <CheckCircle2 className="w-10 h-10 text-neon mb-2" />
                    <h1 className="font-display font-black text-3xl">Draft Complete!</h1>
                    <p className="text-zinc-400 mt-1">Mode: {draft.mode}</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(picksByParticipant).map(([pid, teamIds]) => (
                        <div key={pid} className="brutal-card p-4" data-testid={`final-picks-${pid}`}>
                            <p className="font-bold mb-2 flex items-center gap-2">
                                {participantsById[pid]?.is_admin && <Crown className="w-3 h-3 text-cyber" />}
                                {participantsById[pid]?.username}
                            </p>
                            <div className="space-y-1">
                                {teamIds.map(tid => (
                                    <div key={tid} className="text-sm">
                                        {teamsById[tid]?.flag} {teamsById[tid]?.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // ACTIVE DRAFT
    const isMyTurn = draft.mode === "live" ? currentPickerId === me?.id : (() => {
        const myPicks = draft.picks.filter(pk => pk.participant_id === me?.id).length;
        return myPicks < data.league.teams_per_participant;
    })();

    const availableTeams = data.tournament.teams.filter(t => draft.available_team_ids.includes(t.id));
    const myPickCount = draft.picks.filter(pk => pk.participant_id === me?.id).length;

    return (
        <div className="min-h-screen px-4 lg:px-12 py-6 max-w-7xl mx-auto">
            <header className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <Link to={`/league/${leagueId}`} className="brutal-btn-ghost p-2" data-testid="draft-back-btn-3">
                        <ArrowLeft className="w-4 h-4" />
                    </Link>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">Draft Mode: {draft.mode}</p>
                        <h1 className="font-display font-black text-2xl">Draft Room</h1>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="live-dot" />
                    <span className="text-sm font-bold">LIVE</span>
                </div>
            </header>

            {/* Current picker banner */}
            {draft.mode === "live" && (
                <div className={`brutal-card p-5 mb-4 ${isMyTurn ? "bg-cyber/20 border-cyber animate-pulse-ring" : ""}`} data-testid="current-picker-banner">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {isMyTurn ? "Your turn!" : `${participantsById[currentPickerId]?.username || "—"} is picking`}
                    </p>
                    <p className="font-display font-black text-2xl mt-1">
                        {isMyTurn ? "PICK A TEAM" : "Wait your turn..."}
                    </p>
                </div>
            )}

            {draft.mode === "async" && (
                <div className="brutal-card p-4 mb-4" data-testid="async-banner">
                    <p className="text-sm">
                        <span className="font-bold">Async draft</span> — pick any available team. You have {data.league.teams_per_participant - myPickCount} pick(s) left.
                    </p>
                </div>
            )}

            <div className="grid lg:grid-cols-3 gap-4">
                {/* Available teams */}
                <div className="lg:col-span-2">
                    <h2 className="font-display font-extrabold text-base uppercase tracking-tight mb-3">Available Teams ({availableTeams.length})</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {availableTeams.map(t => (
                            <button key={t.id}
                                data-testid={`pick-team-${t.id}`}
                                onClick={() => handlePick(t.id)}
                                disabled={!isMyTurn}
                                className={`p-3 border-2 rounded-md text-left transition-all ${isMyTurn ? "border-[#333] bg-ink hover:border-cyber hover:bg-cyber/10 cursor-pointer" : "border-[#222] bg-ink/50 opacity-50 cursor-not-allowed"}`}>
                                <div className="text-xl mb-1">{t.flag || "🏳️"}</div>
                                <div className="text-sm font-bold truncate">{t.name}</div>
                                {t.group && <div className="text-[10px] text-zinc-500">Group {t.group}</div>}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Order + picks */}
                <div className="space-y-4">
                    {draft.mode === "live" && (
                        <div className="brutal-card p-4">
                            <h2 className="font-display font-extrabold text-sm uppercase mb-3">Draft Order</h2>
                            <ol className="space-y-1.5">
                                {draft.order.map((pid, idx) => (
                                    <li key={pid} data-testid={`draft-order-${idx}`}
                                        className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-md ${currentPickerId === pid ? "bg-cyber text-black font-bold" : "text-zinc-400"}`}>
                                        <span className="font-mono">{idx + 1}.</span>
                                        <span>{participantsById[pid]?.username}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    <div className="brutal-card p-4">
                        <h2 className="font-display font-extrabold text-sm uppercase mb-3">Recent Picks</h2>
                        <div className="space-y-1.5 max-h-96 overflow-y-auto">
                            {[...draft.picks].reverse().slice(0, 20).map((pk, i) => (
                                <div key={`${pk.participant_id}-${pk.team_id}-${i}`} className="text-xs flex items-center justify-between gap-2 border-b border-[#222] pb-1.5" data-testid={`pick-history-${i}`}>
                                    <span className="font-bold truncate">{participantsById[pk.participant_id]?.username}</span>
                                    <span className="text-zinc-400 text-right truncate">
                                        {teamsById[pk.team_id]?.flag} {teamsById[pk.team_id]?.name}
                                    </span>
                                </div>
                            ))}
                            {draft.picks.length === 0 && <p className="text-zinc-500 text-xs">No picks yet.</p>}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


